import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { CheckCircle, Image as ImageIcon, UploadCloud, X } from 'lucide-react';

import { Button, Modal } from './ui';
import { cn } from '../lib/cn';
import { normalizeImageFile } from '../utils/normalizeImage';
import { uploadImageFile, UploadFlowError } from '../utils/uploadFlow';
import { copyFor, type QuotaExceededInfo } from './QuotaExceededModal';
import type { ImageMeta } from '../types/api';

/**
 * ImageUploadModal — multi-file picker + drag/drop + sequential upload with
 * per-file progress. Wraps the existing backend endpoint
 * POST /api/v1/org/:orgId/projects/:id/images.
 *
 * Per-file lifecycle:
 *   queued       (selected, not started)
 *   uploading    (POST in flight)
 *   done         (server returned ImageMeta)
 *   error        (rejected by server or network failed)
 *
 * Server errors mapped via APIClientError:
 *   `image_quota_exceeded`     → per-file error tag (clarifies which
 *     image hit the cap so the user can remove it and retry the rest)
 *   `image_too_large`          → per-file
 *   `unsupported_image_type`   → per-file
 *
 * Successful uploads call `onUploaded(meta)` one-by-one so the parent can
 * update local state incrementally (don't wait for the whole batch).
 */
interface ImageUploadModalProps {
    open: boolean;
    onClose: () => void;
    orgId?: string;
    projectId: string;
    isDarkMode?: boolean;
    onUploaded?: (image: ImageMeta) => void;
    /** Server-side per-image size cap in MB; from MAX_IMAGE_SIZE_MB env. */
    maxImageSizeMB?: number;
}

interface QueuedFile {
    id: string;
    file: File;
    previewUrl: string;
    status: 'queued' | 'uploading' | 'done' | 'error';
    error?: string;
}

const ACCEPTED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];

/** Human-readable byte size — falls back to MB once the value crosses 1 MB,
 *  otherwise shows KB so small images don't read "0.01 MB". */
function formatBytes(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
}

export default function ImageUploadModal({
    open,
    onClose,
    orgId,
    projectId,
    isDarkMode = false,
    onUploaded,
    maxImageSizeMB = 15,
}: ImageUploadModalProps) {
    const { t } = useTranslation('modals');
    const { t: tCommon } = useTranslation('common');
    const { t: tErrors } = useTranslation('errors');

    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const [queue, setQueue] = useState<QueuedFile[]>([]);
    const [isDragging, setIsDragging] = useState(false);
    const [uploading, setUploading] = useState(false);
    // D.2.3 — when any file in the batch hits annotation_quota_exceeded,
    // show ONE modal explaining the cap instead of per-row error tags
    // (the whole batch is blocked, not just one file).
    /** Inline banner copy for annotation_quota_exceeded — renders inside
     *  this modal (no second dialog). copyFor() maps QuotaExceededInfo
     *  to friendly title + body. */
    const [quotaBanner, setQuotaBanner] = useState<{ title: string; body: string } | null>(null);

    // Cleanup blob URLs on unmount or modal close.
    useEffect(() => {
        return () => {
            queue.forEach((q) => URL.revokeObjectURL(q.previewUrl));
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    useEffect(() => {
        if (!open) {
            queue.forEach((q) => URL.revokeObjectURL(q.previewUrl));
            setQueue([]);
            setUploading(false);
            setIsDragging(false);
            setQuotaBanner(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const addFiles = useCallback(
        (files: FileList | File[]) => {
            const incoming = Array.from(files);
            const accepted: QueuedFile[] = [];
            const rejected: string[] = [];
            for (const f of incoming) {
                if (!ACCEPTED_TYPES.includes(f.type)) {
                    rejected.push(`${f.name}: ${t('image_type_rejected', { defaultValue: 'unsupported format' })}`);
                    continue;
                }
                if (f.size > maxImageSizeMB * 1024 * 1024) {
                    rejected.push(`${f.name}: ${t('image_too_large', { defaultValue: `over ${maxImageSizeMB} MB` })}`);
                    continue;
                }
                accepted.push({
                    id: `${Date.now()}-${Math.random().toString(16).slice(2)}-${f.name}`,
                    file: f,
                    previewUrl: URL.createObjectURL(f),
                    status: 'queued',
                });
            }
            if (rejected.length > 0) {
                toast.error(rejected.join('\n'));
            }
            setQueue((prev) => [...prev, ...accepted]);
        },
        [maxImageSizeMB, t],
    );

    function removeQueued(id: string) {
        setQueue((prev) => {
            const next = prev.filter((q) => q.id !== id);
            const removed = prev.find((q) => q.id === id);
            if (removed) URL.revokeObjectURL(removed.previewUrl);
            return next;
        });
    }

    async function startUpload() {
        if (queue.length === 0 || uploading) return;
        setUploading(true);

        // Capture pending IDs at the start so the worker pool walks a
        // stable list even as `setQueue` updates run. Workers pull from
        // a shared cursor (closure-shared `index`) so each file is
        // processed exactly once.
        const pending = queue.filter((q) => q.status !== 'done');
        let cursor = 0;
        let successCount = 0;

        // Browser caps same-origin connections at ~6. The presign-PUT-
        // finalize flow does 2 PUTs (original + thumb) per file plus 2
        // JSON calls — so 5 concurrent files keeps the pipe full
        // without overflowing into queueing limbo. Tunable if needed.
        const CONCURRENCY = Math.min(5, pending.length);

        async function processOne(q: QueuedFile) {
            setQueue((prev) => prev.map((p) => (p.id === q.id ? { ...p, status: 'uploading', error: undefined } : p)));
            try {
                const safeFile = await normalizeImageFile(q.file);
                const meta = await uploadImageFile(projectId, safeFile, orgId);
                setQueue((prev) => prev.map((p) => (p.id === q.id ? { ...p, status: 'done' } : p)));
                onUploaded?.(meta);
                successCount += 1;
            } catch (err) {
                let msg = tErrors('generic');
                if (err instanceof UploadFlowError) {
                    // D.2.3 — structured annotation_quota_exceeded shows as
                    // an inline banner inside THIS modal (no second dialog
                    // — keeps the user in the upload context). Per-file row
                    // tag is the short label; the banner carries the
                    // customer-facing explanation.
                    if (err.apiCode === 'annotation_quota_exceeded'
                        && err.scope && err.current !== undefined && err.limit !== undefined) {
                        const info: QuotaExceededInfo = {
                            scope: err.scope,
                            current: err.current,
                            limit: err.limit,
                        };
                        setQuotaBanner(
                            copyFor(info) ?? {
                                title: 'Limit reached',
                                body: `A platform limit was reached (${info.scope}: ${info.current} / ${info.limit}). Contact your administrator.`,
                            },
                        );
                        msg = tErrors('annotation_quota_exceeded', { defaultValue: 'Quota exceeded' });
                    }
                    else if (err.apiCode === 'image_too_large') msg = tErrors('image_too_large', { defaultValue: `Image too large (max ${maxImageSizeMB} MB)` });
                    else if (err.apiCode === 'unsupported_image_type') msg = tErrors('unsupported_image_type', { defaultValue: 'Unsupported image type' });
                    else if (err.apiCode === 'upload_put_failed') msg = tErrors('upload_put_failed', { defaultValue: 'Upload failed — please retry' });
                    else if (err.fields && err.fields.length > 0) {
                        // Structured invalid_input — surface the actual field
                        // issue (e.g. "not a decodable image: unsupported
                        // JPEG feature: luma/chroma subsampling ratio").
                        msg = err.fields.map((f) => f.issue).join('; ');
                    } else {
                        msg = err.message;
                    }
                }
                setQueue((prev) => prev.map((p) => (p.id === q.id ? { ...p, status: 'error', error: msg } : p)));
            }
        }

        async function worker() {
            while (true) {
                const idx = cursor++;
                if (idx >= pending.length) return;
                await processOne(pending[idx]);
            }
        }

        // Spawn N workers; each finishes when the shared cursor passes
        // the end. Promise.all resolves when every worker drains —
        // i.e. every pending file has terminal status.
        await Promise.all(Array.from({ length: CONCURRENCY }, () => worker()));

        setUploading(false);
        if (successCount > 0) {
            toast.success(t('images_uploaded', { defaultValue: `Uploaded ${successCount} image(s)`, count: successCount }));
        }
    }

    function onPickFiles(e: React.ChangeEvent<HTMLInputElement>) {
        if (e.target.files) addFiles(e.target.files);
        e.target.value = ''; // reset so picking same file twice still fires
    }

    function onDrop(e: React.DragEvent<HTMLDivElement>) {
        e.preventDefault();
        setIsDragging(false);
        if (e.dataTransfer.files) addFiles(e.dataTransfer.files);
    }

    // After upload runs, successful rows are hidden from the list so the
    // user sees ONLY what still needs attention (queued or failed). The
    // visible queue thus answers the question "what didn't upload?" at a
    // glance — the previous mixed display was the source of the confusion.
    const visibleQueue = queue.filter((q) => q.status !== 'done');
    const doneCount = queue.length - visibleQueue.length;
    const failedCount = queue.filter((q) => q.status === 'error').length;
    const anyVisible = visibleQueue.length > 0;
    const allUploadsSucceeded = queue.length > 0 && visibleQueue.length === 0 && failedCount === 0;

    function removeAllDone() {
        setQueue((prev) => {
            prev.forEach((q) => { if (q.status === 'done') URL.revokeObjectURL(q.previewUrl); });
            return prev.filter((q) => q.status !== 'done');
        });
    }
    function clearAll() {
        queue.forEach((q) => URL.revokeObjectURL(q.previewUrl));
        setQueue([]);
    }

    return (
        <>
        <Modal
            open={open}
            onClose={uploading ? () => {} : onClose}
            title={t('upload_images_title', { defaultValue: 'Upload images' })}
            widthClass="max-w-2xl"
            isDarkMode={isDarkMode}
            footer={
                <>
                    <Button
                        variant="ghost"
                        isDarkMode={isDarkMode}
                        onClick={onClose}
                        disabled={uploading}
                    >
                        {allUploadsSucceeded ? tCommon('close', { defaultValue: 'Done' }) : tCommon('cancel')}
                    </Button>
                    {anyVisible && (
                        <Button
                            variant="success"
                            leftIcon={<UploadCloud className="h-4 w-4" />}
                            onClick={() => void startUpload()}
                            disabled={uploading || visibleQueue.length === 0}
                        >
                            {uploading
                                ? t('uploading', { defaultValue: 'Uploading…' })
                                : failedCount > 0
                                    ? t('retry_n_files', {
                                        defaultValue: `Retry ${visibleQueue.length} file(s)`,
                                        count: visibleQueue.length,
                                    })
                                    : t('upload_n_files', {
                                        defaultValue: `Upload ${visibleQueue.length} file(s)`,
                                        count: visibleQueue.length,
                                    })}
                        </Button>
                    )}
                </>
            }
        >
            <div className="space-y-4">
                {/* Quota banner — friendly inline error when the server
                    returns annotation_quota_exceeded. Stays in the same
                    modal (no second dialog) so the user keeps context. */}
                {quotaBanner && (
                    <div
                        role="alert"
                        className={cn(
                            'rounded-lg border p-3',
                            isDarkMode
                                ? 'border-red-800 bg-red-900/20'
                                : 'border-red-200 bg-red-50',
                        )}
                    >
                        <p
                            className={cn(
                                'text-sm font-semibold',
                                isDarkMode ? 'text-red-300' : 'text-red-800',
                            )}
                        >
                            {quotaBanner.title}
                        </p>
                        <p
                            className={cn(
                                'text-xs mt-1',
                                isDarkMode ? 'text-red-300/80' : 'text-red-700',
                            )}
                        >
                            {quotaBanner.body}
                        </p>
                    </div>
                )}

                {/* All-success panel — replaces the drop zone + list when every
                     queued file uploaded cleanly. One clear "Done" affordance,
                     no confusing residual rows. */}
                {allUploadsSucceeded ? (
                    <div className={cn(
                        'flex flex-col items-center justify-center gap-3 rounded-lg border px-6 py-10 text-center',
                        isDarkMode ? 'border-emerald-700/50 bg-emerald-900/15' : 'border-emerald-200 bg-emerald-50',
                    )}>
                        <CheckCircle className={cn('h-10 w-10', isDarkMode ? 'text-emerald-400' : 'text-emerald-500')} />
                        <p className={cn('text-base font-semibold', isDarkMode ? 'text-emerald-200' : 'text-emerald-900')}>
                            {t('all_uploaded_title', {
                                defaultValue: 'All {{count}} image(s) uploaded',
                                count: doneCount,
                            })}
                        </p>
                        <p className={cn('text-sm', isDarkMode ? 'text-emerald-300/80' : 'text-emerald-700')}>
                            {t('all_uploaded_sub', { defaultValue: 'You can close this dialog or add more files.' })}
                        </p>
                        <div className="flex gap-2 mt-2">
                            <Button variant="ghost" isDarkMode={isDarkMode} onClick={clearAll}>
                                {t('add_more', { defaultValue: 'Add more' })}
                            </Button>
                        </div>
                    </div>
                ) : (
                    <>
                        {/* Drop zone */}
                        <div
                            onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                            onDragLeave={() => setIsDragging(false)}
                            onDrop={onDrop}
                            onClick={() => fileInputRef.current?.click()}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') fileInputRef.current?.click(); }}
                            className={cn(
                                'flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed cursor-pointer transition-colors duration-150 px-6 py-10 text-center',
                                isDragging
                                    ? isDarkMode ? 'border-primary-400 bg-primary-500/10' : 'border-primary-500 bg-primary-50'
                                    : isDarkMode ? 'border-gray-700 hover:border-gray-600 hover:bg-gray-800/30' : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50',
                            )}
                        >
                            <UploadCloud className={cn('h-8 w-8', isDarkMode ? 'text-gray-400' : 'text-gray-400')} />
                            <p className={cn('text-sm font-medium', isDarkMode ? 'text-gray-200' : 'text-gray-700')}>
                                {t('drop_zone_primary', { defaultValue: 'Drag images here, or click to select' })}
                            </p>
                            <p className={cn('text-xs', isDarkMode ? 'text-gray-500' : 'text-gray-500')}>
                                {t('drop_zone_subtext', { defaultValue: `JPG, PNG, or WebP · up to ${maxImageSizeMB} MB each`, max: maxImageSizeMB })}
                            </p>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept={ACCEPTED_TYPES.join(',')}
                                multiple
                                className="hidden"
                                onChange={onPickFiles}
                            />
                        </div>

                        {/* Summary chip for partial success — appears when some files
                             succeeded but others remain visible (queued + failed). */}
                        {doneCount > 0 && (
                            <div className={cn(
                                'flex items-center justify-between gap-3 rounded-md border px-3 py-2 text-sm',
                                isDarkMode ? 'border-emerald-700/40 bg-emerald-900/15 text-emerald-200' : 'border-emerald-200 bg-emerald-50 text-emerald-800',
                            )}>
                                <span className="flex items-center gap-2">
                                    <CheckCircle className="h-4 w-4 shrink-0" />
                                    {t('uploaded_count', {
                                        defaultValue: '{{count}} uploaded successfully',
                                        count: doneCount,
                                    })}
                                </span>
                                <button
                                    type="button"
                                    onClick={removeAllDone}
                                    className={cn(
                                        'text-xs font-medium underline-offset-2 hover:underline',
                                        isDarkMode ? 'text-emerald-300' : 'text-emerald-700',
                                    )}
                                >
                                    {t('clear_done', { defaultValue: 'Clear' })}
                                </button>
                            </div>
                        )}

                        {/* Queue — only rows that need user attention */}
                        {visibleQueue.length > 0 && (
                            <ul className="space-y-2 max-h-[40vh] overflow-y-auto pr-1">
                                {visibleQueue.map((q) => (
                                    <li
                                        key={q.id}
                                        className={cn(
                                            'flex items-center gap-3 rounded-lg border p-2',
                                            q.status === 'error'
                                                ? isDarkMode ? 'border-red-800/60 bg-red-900/15' : 'border-red-200 bg-red-50'
                                                : isDarkMode ? 'border-gray-700 bg-gray-800/40' : 'border-gray-200 bg-gray-50',
                                        )}
                                    >
                                        <div className={cn('h-12 w-12 shrink-0 rounded-md overflow-hidden flex items-center justify-center', isDarkMode ? 'bg-gray-700' : 'bg-gray-200')}>
                                            {q.previewUrl ? (
                                                <img src={q.previewUrl} alt="" className="h-full w-full object-cover" />
                                            ) : (
                                                <ImageIcon className={cn('h-5 w-5', isDarkMode ? 'text-gray-500' : 'text-gray-400')} />
                                            )}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className={cn('text-sm truncate', isDarkMode ? 'text-gray-200' : 'text-gray-800')}>{q.file.name}</p>
                                            <p className={cn('text-xs', q.status === 'error' ? 'text-red-500' : isDarkMode ? 'text-gray-500' : 'text-gray-500')}>
                                                {/* File size is shown in every state (queued / uploading
                                                    / error) so the user can correlate progress with
                                                    image weight, and confirm what's about to be sent
                                                    at a glance. */}
                                                {formatBytes(q.file.size)}
                                                {q.status === 'uploading' && (
                                                    <span> · {t('uploading', { defaultValue: 'Uploading…' })}</span>
                                                )}
                                                {q.status === 'error' && (
                                                    <span className="text-red-500"> · {q.error ?? tErrors('generic')}</span>
                                                )}
                                            </p>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => removeQueued(q.id)}
                                            disabled={q.status === 'uploading'}
                                            className={cn(
                                                'p-1.5 rounded-md transition-colors',
                                                isDarkMode ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-200 text-gray-500',
                                                q.status === 'uploading' && 'opacity-30 cursor-not-allowed',
                                            )}
                                            aria-label={tCommon('delete')}
                                        >
                                            <X className="h-4 w-4" />
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </>
                )}
            </div>
        </Modal>
        </>
    );
}
